// Cross-references what GetLoadedFamilies reports against a catalog family, so the grid and the
// detail dialog can say "this is already in the project" without the Revit side knowing anything
// about slugs.
//
// The match key is a Revit-assigned family NAME, not the slug: DownloadAndLoad names the file it
// loads after the catalog family's display name, sanitised for the filesystem
// (`SanitizeFileName` in CatalogService.cs), and Revit takes the loaded family's name straight from
// that file name. So the same sanitisation is reproduced here rather than adding a field to
// FamilySummaryDto for it — CatalogService.cs is out of scope for this change (see the report), and
// the rule itself is four lines that are cheap to keep in sync by hand.

import type { LoadedFamily } from "./types";

// catalogStore's state is a plain mutable $state proxy (no readonly() equivalent in Svelte) — a
// plain LoadedFamily[] is exactly what call sites reading loadedFamilies pass here.
type LoadedFamilyList = LoadedFamily[];

// Path.GetInvalidFileNameChars() on Windows: the nine punctuation characters plus every ASCII
// control character. Revit only ever runs on Windows, so there is no second rule set to mirror.
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\x00-\x1f]/g;

/** Mirrors CatalogService.SanitizeFileName — what a catalog family's display name becomes on disk,
 *  and therefore what Revit names the loaded family after. */
export function sanitizeFileName(name: string): string {
  const cleaned = name.replace(INVALID_FILENAME_CHARS, "_").trim();
  return cleaned.length === 0 ? "family" : cleaned;
}

/** The loaded-family row matching a catalog family's display name, or undefined when it is not in
 *  the active document. */
export function findLoadedFamily(loaded: LoadedFamilyList, catalogName: string) {
  const expected = sanitizeFileName(catalogName).toLowerCase();
  return loaded.find((f) => f.name.toLowerCase() === expected);
}

/** Whether this exact type of a catalog family is among its loaded types. False when the family
 *  itself is not loaded at all. */
export function isTypeLoaded(loaded: LoadedFamilyList, catalogName: string, typeName: string): boolean {
  const match = findLoadedFamily(loaded, catalogName);
  if (!match) return false;
  return match.types.some((t) => t.toLowerCase() === typeName.toLowerCase());
}
